import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, ShieldCheck, Ban, Loader2 } from "lucide-react";
import { API_BASE as API_URL } from "@/config/api";

const Metric = ({ label, m }) => (
  <div className="text-xs text-muted-foreground">
    <span className="font-semibold text-foreground">{label}:</span> точность top1 {m.top1}% · top3 {m.top3}% · ACPL {m.acpl} (норма ≈{m.expected_acpl}) · зевков {m.blunders}
    {m.uniform_timing && " · ровное время"} · <span className={m.score >= 70 ? "text-red-500 font-bold" : ""}>риск {m.score}</span>
  </div>
);

export const AdminFairPlay = ({ token }) => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(null);
  const headers = { Authorization: `Bearer ${token}` };

  const load = async () => {
    try {
      const res = await axios.get(`${API_URL}/admin/fairplay/flagged`, { headers });
      setItems(res.data);
    } catch (e) {
      toast.error("Не удалось загрузить список");
      setItems([]);
    }
  };
  useEffect(() => { load(); }, []);

  const resolve = async (userId, action) => {
    setBusy(userId + action);
    try {
      await axios.post(`${API_URL}/admin/fairplay/${userId}/resolve?action=${action}`, {}, { headers });
      toast.success(action === "clear" ? "Флаг снят, вывод разморожен" : "Аккаунт заблокирован");
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Ошибка");
    } finally {
      setBusy(null);
    }
  };

  if (items === null) return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-3" data-testid="admin-fairplay">
      <p className="text-sm text-muted-foreground">
        Все рейтинговые, турнирные и матчмейкинг-партии анализируются Stockfish автоматически: во время игры (с 3-го хода) и полностью после окончания. Игроки с риском ≥ 70 попадают сюда, их вывод средств заморожен до вашего решения.
      </p>
      {items.length === 0 && (
        <Card className="glass"><CardContent className="py-10 text-center text-muted-foreground flex items-center justify-center gap-2">
          <ShieldCheck className="h-5 w-5 text-green-500" /> Подозрительных игроков нет
        </CardContent></Card>
      )}
      {items.map((u) => (
        <Card key={u.id} className="glass border-yellow-500/30" data-testid={`fairplay-user-${u.id}`}>
          <CardContent className="p-4 space-y-2">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <ShieldAlert className="h-5 w-5 text-yellow-500" />
                <span className="font-semibold">{u.username}</span>
                <span className="text-xs text-muted-foreground">рейтинг {u.rating} · баланс {Math.round(u.balance || 0)} P</span>
                <Badge variant="outline" className="text-xs">флагов: {u.fairplay_flags_count || 1}</Badge>
                {u.withdrawal_hold && <Badge className="bg-red-500/20 text-red-500 border-red-500/30 text-xs">вывод заморожен</Badge>}
              </div>
              <div className="flex gap-2">
                <Button size="sm" variant="outline" disabled={!!busy} onClick={() => resolve(u.id, "clear")} data-testid={`fairplay-clear-${u.id}`}>
                  {busy === u.id + "clear" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}Честный — снять
                </Button>
                <Button size="sm" variant="destructive" disabled={!!busy} onClick={() => resolve(u.id, "ban")} data-testid={`fairplay-ban-${u.id}`}>
                  {busy === u.id + "ban" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Ban className="h-4 w-4 mr-1" />}Читер — бан
                </Button>
              </div>
            </div>
            {u.report && (
              <div className="space-y-1 pt-1 border-t border-border/50">
                <p className="text-xs text-muted-foreground">Партия {u.report.game_id?.slice(0, 8)} · {new Date(u.report.created_at).toLocaleString("ru-RU")}</p>
                {u.report.metrics && Object.entries(u.report.metrics).map(([c, m]) => (
                  <Metric key={c} label={c === "white" ? "Белые" : "Чёрные"} m={m} />
                ))}
                <p className="text-sm italic">{u.report.analysis}</p>
              </div>
            )}
          </CardContent>
        </Card>
      ))}
    </div>
  );
};

export default AdminFairPlay;
